import { CatalogueDataType } from 'src/lib/types/common/catalogue'
import {
  DiscountFormV2StateType,
  CustomerEligibilityFormV2Type,
  CustomerEligibilityV2Type,
  DiscountStackMode,
  DiscountStackabilityFormV2Type,
  UsageLimitFormV2Type,
  AvailabilityFormV2Type,
  DiscountBackendDataV2,
  DiscountMethod,
  DiscountApplicationMethodFormV2Type,
  DiscountPreviewV2Type,
  CouponType,
  bxgyProductStrategy,
  generationSourceType,
  AmountType,
  UiConfigDiscountType,
  DiscountApplyToType,
  EntitledCatalogueEntity,
  EntitledCatalogueEntityType,
  ApplicationTarget,
  PurchaseConditionAmountType,
  PrerequisiteType,
  ExcludedCatalogueType
} from 'src/lib/types/discountsV2'
import { MerchantAttributesType } from 'src/lib/types/merchantAttributes'

type PrerequisiteDistributionType = NonNullable<DiscountBackendDataV2['prerequisite_entity_quantity_distribution']>

const getEntitiesForPayload = (entities: any[], metadata?: CatalogueDataType[]) => {
  if (metadata?.[0]) {
    if (metadata?.[0]?.id === 'shopflo-all-products') {
      return []
    }
    if (metadata?.[0]?.id === 'shopflo-all-collections') {
      return []
    }
  }
  return entities?.map(entity => ({
    type: entity.type,
    entity_id: entity.entityId
  }))
}

const getExcludedCatalogueForPayload = (
  excludedCatalogue: ExcludedCatalogueType[],
  metadata?: CatalogueDataType[][]
) => {
  if (excludedCatalogue?.[0] && excludedCatalogue?.[0]?.entities?.length === 0) {
    return []
  }
  return excludedCatalogue?.map((group, index) => ({
    quantity: 1,
    entities: getEntitiesForPayload(Array.isArray(group) ? [] : group?.entities, metadata?.[index])
  }))
}

const getEntitlementExcludedForPayload = (state: DiscountFormV2StateType) => {
  const { entitlement } = state
  if (entitlement.type === CouponType.SHIPPING) return []
  return getExcludedCatalogueForPayload(entitlement.excludedCatalogue ?? [], entitlement.metaData?.excludedCatalogue) ?? []
}

const getPrerequisiteExcludedForPayload = (state: DiscountFormV2StateType) => {
  const { prerequisite } = state
  if (prerequisite.type === CouponType.BUNDLE) return []
  return getExcludedCatalogueForPayload(prerequisite.excludedCatalogue ?? [], prerequisite.metaData?.excludedCatalogue) ?? []
}

export const createDiscountPayloadV2 = (
  state: DiscountFormV2StateType,
  merchant: MerchantAttributesType | null,
  isEditMode?: boolean
) => {
  const applicationMethod = createApplicationMethodPayload(state.entitlement.applicationMethod)
  const applicationRules = createDiscountApplicationRulesPayload(state, isEditMode)
  const customerEligibility = createCustomerEligibilityPayload(state.prerequisite.customerEligibility)
  const discountStackability = createDiscountStackabilityPayload(state.prerequisite.stackability)
  const usageLimit = createUsageLimitPayload(state.prerequisite.usageLimit, isEditMode)
  const availability = createAvailabilityPayload(state.prerequisite.availability, isEditMode)
  const preview = createPreviewPayload(state.configuration.preview)

  return {
    ...applicationMethod,
    ...applicationRules,
    ...customerEligibility,
    ...discountStackability,
    ...usageLimit,
    ...availability,
    ...preview,
    // Exit coupons must never be discoverable or auto-applicable — they are triggered programmatically on exit intent
    ...(state.uiConfig.type === UiConfigDiscountType.EXIT_COUPON ? { is_discoverable: false, is_auto_applicable: false } : {}),
    tags: ['SHOPFLO'],
    metadata: {
      coveredCatalogue: [],
      excludedCatalogue: [],
      ...applicationRules?.metadata
    },
    created_by: merchant?.attributes?.shopName
  }
}

const createDiscountApplicationRulesPayload = (state: DiscountFormV2StateType, isEditMode?: boolean) => {
  const { uiConfig, entitlement, prerequisite } = state

  if (uiConfig.type === UiConfigDiscountType.BUNDLE) {
    return getBundleDiscountPayload(state)
  }

  if (uiConfig.type === UiConfigDiscountType.BXGY) {
    return getBxgyDiscountPayload(state, isEditMode)
  }

  if (uiConfig.type === UiConfigDiscountType.BXGY_AUTO_ADDED) {
    return getBxgyAutoAddedDiscountPayload(state, isEditMode)
  }

  if (uiConfig.type === UiConfigDiscountType.AMOUNT_OFF_PRODUCTS) {
    return getAmountOffProductsDiscountPayload(state, isEditMode)
  }

  if (uiConfig.type === UiConfigDiscountType.AMOUNT_OFF) {
    return getAmountOffDiscountPayload(state, isEditMode)
  }

  if (uiConfig.type === UiConfigDiscountType.AMOUNT_OFF_UPTO) {
    return getAmountOffUptoDiscountPayload(state, isEditMode)
  }

  if (uiConfig.type === UiConfigDiscountType.SHIPPING) {
    return getShippingDiscountPayload(state, isEditMode)
  }

  if (uiConfig.type === UiConfigDiscountType.FREEBIE || uiConfig.type === UiConfigDiscountType.FREEBIE_SELECTOR) {
    return getFreebieDiscountPayload(state, isEditMode)
  }

  if (uiConfig.type === UiConfigDiscountType.EXIT_COUPON) {
    return getExitCouponDiscountPayload(state, isEditMode)
  }
}

const getBxgyDiscountPayload = (state: DiscountFormV2StateType, isEditMode?: boolean) => {
  const { entitlement, prerequisite } = state
  if (entitlement.type !== CouponType.AMOUNT || prerequisite.type === CouponType.BUNDLE) return

  let prerequisiteDistribution: PrerequisiteDistributionType = []
  let prerequisiteSubtotalRange = null
  let prerequisiteMaxOrderValue = null
  let prerequisiteCountToEntitlement = null
  let prerequisiteMaxCountForEntitlement = null
  let prerequisiteCatalboueMetaData: CatalogueDataType[][] = []

  // Common mapping for all types
  prerequisiteDistribution = prerequisite.prerequisiteCatalogue.map((item, index) => ({
    ...(item.type === PrerequisiteType.PRICE
      ? { price: item.price, max_price: item.maxPrice }
      : { quantity: item.quantity, max_quantity: item.maxQuantity }),
    entities: getEntitiesForPayload(item.entities, prerequisite.metaData.prerequisiteCatalogue[index])
  }))
  prerequisiteCatalboueMetaData = prerequisite.metaData.prerequisiteCatalogue

  if (prerequisite.prerequisiteType === PrerequisiteType.PRICE) {
    if (prerequisite.purchaseConditionAmountType === PurchaseConditionAmountType.ENTIRE_CART) {
      prerequisiteSubtotalRange = prerequisite.prerequisiteSubtotalRange
      prerequisiteMaxOrderValue = prerequisite.prerequisiteMaxOrderValue
      prerequisiteDistribution = []
      prerequisiteCatalboueMetaData = []
    }
  } else if (prerequisite.prerequisiteType === PrerequisiteType.QUANTITY) {
    if (prerequisite.purchaseConditionAmountType === PurchaseConditionAmountType.ENTIRE_CART) {
      prerequisiteCountToEntitlement = prerequisite.prerequisiteCountToEntitlement
      prerequisiteMaxCountForEntitlement = prerequisite.prerequisiteMaxCountForEntitlement
      prerequisiteDistribution = []
      prerequisiteCatalboueMetaData = []
    }
  }

  return {
    coupon_type: CouponType.AMOUNT,
    amount_type: entitlement.amountType,
    concession_amount: entitlement.concessionAmount,
    bxgy_product_strategy: bxgyProductStrategy.DISCOUNT_ON_PRODUCT,
    generation_source: entitlement.generationSource,
    entitled_entity_quantity_distribution: entitlement.entitledCatalogue.map((group, index) => ({
      quantity: group.quantity,
      entities: getEntitiesForPayload(group.entities, entitlement.metaData.entitledCatalogue[index])
    })),
    excluded_entity_ids: getEntitlementExcludedForPayload(state),
    prerequisite_excluded_entity_ids: getPrerequisiteExcludedForPayload(state),
    entitled_product_count: entitlement.entitledProductCount,
    maximum_concession_amount: null,
    application_target: ApplicationTarget.ENTITLED,
    prerequisite_subtotal_range: prerequisiteSubtotalRange ?? (isEditMode ? -1 : null),
    prerequisite_max_order_value: prerequisiteMaxOrderValue ?? (isEditMode ? -1 : null),
    prerequisite_count_to_entitlement:
      prerequisite.prerequisiteType === PrerequisiteType.QUANTITY
        ? prerequisite.prerequisiteCountToEntitlement ?? (isEditMode ? -1 : null)
        : isEditMode
        ? -1
        : null,
    prerequisite_max_count_for_entitlement: prerequisiteMaxCountForEntitlement ?? (isEditMode ? -1 : null),
    prerequisite_entity_quantity_distribution: prerequisiteDistribution,
    multiple_application_options: {
      enable: Boolean(entitlement.maxUsesPerOrder),
      number_of_times_to_apply: entitlement.maxUsesPerOrder ?? null
    },
    metadata: {
      entitledCatalogue: entitlement.metaData.entitledCatalogue,
      excludedCatalogue: entitlement.metaData.excludedCatalogue,
      prerequisiteCatalogue: prerequisiteCatalboueMetaData,
      prerequisiteExcludedCatalogue: prerequisite.metaData?.excludedCatalogue || []
    }
  }
}

const getFreebieDiscountPayload = (state: DiscountFormV2StateType, isEditMode?: boolean) => {
  const { entitlement, prerequisite } = state
  if (entitlement.type !== CouponType.BXGY || prerequisite.type === CouponType.BUNDLE) return

  let prerequisiteDistribution: PrerequisiteDistributionType = []
  let prerequisiteSubtotalRange = null
  let prerequisiteMaxOrderValue = null
  let prerequisiteCountToEntitlement = null
  let prerequisiteMaxCountForEntitlement = null
  let prerequisiteCatalboueMetaData: CatalogueDataType[][] = []

  prerequisiteDistribution = prerequisite.prerequisiteCatalogue.map((item, index) => ({
    ...(item.type === PrerequisiteType.PRICE
      ? { price: item.price, max_price: item.maxPrice }
      : { quantity: item.quantity, max_quantity: item.maxQuantity }),
    entities: getEntitiesForPayload(item.entities, prerequisite.metaData.prerequisiteCatalogue[index])
  }))
  prerequisiteCatalboueMetaData = prerequisite.metaData.prerequisiteCatalogue

  if (prerequisite.prerequisiteType === PrerequisiteType.PRICE) {
    if (prerequisite.purchaseConditionAmountType === PurchaseConditionAmountType.ENTIRE_CART) {
      prerequisiteSubtotalRange = prerequisite.prerequisiteSubtotalRange
      prerequisiteMaxOrderValue = prerequisite.prerequisiteMaxOrderValue
      prerequisiteDistribution = []
      prerequisiteCatalboueMetaData = []
    }
  } else if (prerequisite.prerequisiteType === PrerequisiteType.QUANTITY) {
    if (prerequisite.purchaseConditionAmountType === PurchaseConditionAmountType.ENTIRE_CART) {
      prerequisiteCountToEntitlement = prerequisite.prerequisiteCountToEntitlement
      prerequisiteMaxCountForEntitlement = prerequisite.prerequisiteMaxCountForEntitlement
      prerequisiteDistribution = []
      prerequisiteCatalboueMetaData = []
    } else {
      prerequisiteCountToEntitlement = prerequisite.prerequisiteCountToEntitlement
    }
  }

  const entitledProductCount = entitlement.entitledProductCount

  return {
    coupon_type: CouponType.BXGY,
    amount_type: AmountType.PERCENTAGE,
    concession_amount: 100,
    bxgy_product_strategy: bxgyProductStrategy.ADD_PRODUCT,
    generation_source: entitlement.generationSource,
    entitled_entity_quantity_distribution: entitlement.entitledCatalogue.map((group, index) => ({
      quantity: group.quantity,
      entities: getEntitiesForPayload(group.entities, entitlement.metaData.entitledCatalogue[index])
    })),
    excluded_entity_ids: getEntitlementExcludedForPayload(state),
    prerequisite_excluded_entity_ids: getPrerequisiteExcludedForPayload(state),
    entitled_product_count: entitledProductCount,
    maximum_concession_amount: null,
    application_target: ApplicationTarget.ENTITLED,
    prerequisite_subtotal_range: prerequisiteSubtotalRange ?? (isEditMode ? -1 : null),
    prerequisite_max_order_value: prerequisiteMaxOrderValue ?? (isEditMode ? -1 : null),
    prerequisite_count_to_entitlement:
      prerequisite.prerequisiteType === PrerequisiteType.QUANTITY
        ? prerequisite.prerequisiteCountToEntitlement ?? (isEditMode ? -1 : null)
        : isEditMode
        ? -1
        : null,
    prerequisite_max_count_for_entitlement: prerequisiteMaxCountForEntitlement ?? (isEditMode ? -1 : null),
    prerequisite_entity_quantity_distribution: prerequisiteDistribution,
    metadata: {
      entitledCatalogue: entitlement.metaData.entitledCatalogue,
      excludedCatalogue: entitlement.metaData.excludedCatalogue,
      prerequisiteCatalogue: prerequisiteCatalboueMetaData,
      prerequisiteExcludedCatalogue: prerequisite.metaData?.excludedCatalogue || []
    },
    multiple_application_options: {
      enable: Boolean(entitlement.maxUsesPerOrder),
      number_of_times_to_apply: entitlement.maxUsesPerOrder ?? null
    }
  }
}

const getBundleDiscountPayload = (state: DiscountFormV2StateType) => {
  const { uiConfig, entitlement } = state
  if (uiConfig.type !== UiConfigDiscountType.BUNDLE || entitlement.type !== CouponType.BUNDLE) return

  return {
    coupon_type: CouponType.BUNDLE,
    amount_type: AmountType.FLAT,
    concession_amount: entitlement.concessionAmount,
    bxgy_product_strategy: bxgyProductStrategy.DISCOUNT_ON_PRODUCT,
    generation_source: generationSourceType.SINGLE,
    entitled_entity_quantity_distribution: entitlement.entitledCatalogue.map((group, index) => ({
      quantity: group.quantity,
      entities: getEntitiesForPayload(group.entities, entitlement.metaData.entitledCatalogue[index])
    })),
    excluded_entity_ids: getEntitlementExcludedForPayload(state),
    prerequisite_excluded_entity_ids: getPrerequisiteExcludedForPayload(state),
    entitled_product_count: entitlement.entitledProductCount,
    multiple_application_options: {
      enable: Boolean(entitlement.maxUsesPerOrder),
      number_of_times_to_apply: entitlement.maxUsesPerOrder ?? null
    },
    application_target:
      entitlement.applyTo === DiscountApplyToType.ALL_PRODUCTS ||
      entitlement.entitledCatalogue.some(group => group.entities.length === 0)
        ? 'ALL'
        : 'ENTITLED',
    metadata: {
      entitledCatalogue: entitlement.metaData.entitledCatalogue,
      excludedCatalogue: entitlement.metaData.excludedCatalogue,
      prerequisiteCatalogue: [],
      prerequisiteExcludedCatalogue: []
    }
  }
}

const getBxgyAutoAddedDiscountPayload = (state: DiscountFormV2StateType, isEditMode?: boolean) => {
  const { entitlement, prerequisite } = state
  if (entitlement.type !== CouponType.BXGY || prerequisite.type === CouponType.BUNDLE) return

  let prerequisiteDistribution: PrerequisiteDistributionType = []
  let prerequisiteSubtotalRange = null
  let prerequisiteMaxOrderValue = null
  let prerequisiteCountToEntitlement = null
  let prerequisiteMaxCountForEntitlement = null

  prerequisiteDistribution = prerequisite.prerequisiteCatalogue.map((item, index) => ({
    ...(item.type === PrerequisiteType.PRICE
      ? { price: item.price, max_price: item.maxPrice }
      : { quantity: item.quantity, max_quantity: item.maxQuantity }),
    entities: getEntitiesForPayload(item.entities, prerequisite.metaData.prerequisiteCatalogue[index])
  }))

  if (prerequisite.prerequisiteType === PrerequisiteType.PRICE) {
    if (prerequisite.purchaseConditionAmountType === PurchaseConditionAmountType.ENTIRE_CART) {
      prerequisiteSubtotalRange = prerequisite.prerequisiteSubtotalRange
      prerequisiteMaxOrderValue = prerequisite.prerequisiteMaxOrderValue
      prerequisiteDistribution = []
    }
  } else if (prerequisite.prerequisiteType === PrerequisiteType.QUANTITY) {
    if (prerequisite.purchaseConditionAmountType === PurchaseConditionAmountType.ENTIRE_CART) {
      prerequisiteCountToEntitlement = prerequisite.prerequisiteCountToEntitlement
      prerequisiteMaxCountForEntitlement = prerequisite.prerequisiteMaxCountForEntitlement
      prerequisiteDistribution = []
    } else {
      prerequisiteCountToEntitlement = prerequisite.prerequisiteCountToEntitlement
    }
  }

  return {
    coupon_type: CouponType.BXGY,
    amount_type: entitlement.amountType,
    concession_amount: entitlement.concessionAmount,
    bxgy_product_strategy: bxgyProductStrategy.ADD_PRODUCT,
    generation_source: generationSourceType.SINGLE,
    entitled_entity_quantity_distribution: entitlement.entitledCatalogue.map((group, index) => ({
      quantity: group.quantity,
      entities: getEntitiesForPayload(group.entities, entitlement.metaData.entitledCatalogue[index])
    })),
    excluded_entity_ids: getEntitlementExcludedForPayload(state),
    prerequisite_excluded_entity_ids: getPrerequisiteExcludedForPayload(state),
    entitled_product_count: entitlement.entitledProductCount,
    application_target: ApplicationTarget.ENTITLED,
    prerequisite_subtotal_range: prerequisiteSubtotalRange ?? (isEditMode ? -1 : null),
    prerequisite_max_order_value: prerequisiteMaxOrderValue ?? (isEditMode ? -1 : null),
    prerequisite_count_to_entitlement:
      prerequisite.prerequisiteType === PrerequisiteType.QUANTITY
        ? prerequisite.prerequisiteCountToEntitlement ?? (isEditMode ? -1 : null)
        : isEditMode
        ? -1
        : null,
    prerequisite_max_count_for_entitlement: prerequisiteMaxCountForEntitlement ?? (isEditMode ? -1 : null),
    prerequisite_entity_quantity_distribution: prerequisiteDistribution,
    multiple_application_options: {
      enable: Boolean(entitlement.maxUsesPerOrder),
      number_of_times_to_apply: entitlement.maxUsesPerOrder
    },
    metadata: {
      entitledCatalogue: entitlement.metaData.entitledCatalogue,
      excludedCatalogue: entitlement.metaData.excludedCatalogue,
      prerequisiteCatalogue: prerequisite.metaData.prerequisiteCatalogue || [],
      prerequisiteExcludedCatalogue: prerequisite.metaData?.excludedCatalogue || []
    }
  }
}

const getAmountOffProductsDiscountPayload = (state: DiscountFormV2StateType, isEditMode?: boolean) => {
  const { entitlement, prerequisite, configuration } = state
  if (entitlement.type !== CouponType.AMOUNT || prerequisite.type === CouponType.BUNDLE) return

  let prerequisiteDistribution: PrerequisiteDistributionType = []
  let prerequisiteSubtotalRange = null
  let prerequisiteMaxOrderValue = null
  let prerequisiteCountToEntitlement = null
  let prerequisiteMaxCountForEntitlement = null
  let prerequisiteCatalboueMetaData: CatalogueDataType[][] = []

  prerequisiteDistribution = prerequisite.prerequisiteCatalogue.map((item, index) => ({
    ...(item.type === PrerequisiteType.PRICE
      ? { price: item.price, max_price: item.maxPrice }
      : { quantity: item.quantity, max_quantity: item.maxQuantity }),
    entities: getEntitiesForPayload(item.entities, prerequisite.metaData.prerequisiteCatalogue[index])
  }))
  prerequisiteCatalboueMetaData = prerequisite.metaData.prerequisiteCatalogue

  if (prerequisite.prerequisiteType === PrerequisiteType.PRICE) {
    if (prerequisite.purchaseConditionAmountType === PurchaseConditionAmountType.ENTIRE_CART) {
      prerequisiteSubtotalRange = prerequisite.prerequisiteSubtotalRange
      prerequisiteMaxOrderValue = prerequisite.prerequisiteMaxOrderValue
      prerequisiteDistribution = []
      prerequisiteCatalboueMetaData = []
    }
  } else if (prerequisite.prerequisiteType === PrerequisiteType.QUANTITY) {
    if (prerequisite.purchaseConditionAmountType === PurchaseConditionAmountType.ENTIRE_CART) {
      prerequisiteCountToEntitlement = prerequisite.prerequisiteCountToEntitlement
      prerequisiteMaxCountForEntitlement = prerequisite.prerequisiteMaxCountForEntitlement
      prerequisiteDistribution = []
      prerequisiteCatalboueMetaData = []
    }
  }

  return {
    coupon_type: configuration.couponType,
    amount_type: entitlement.amountType,
    concession_amount: entitlement.concessionAmount,
    bxgy_product_strategy: bxgyProductStrategy.DISCOUNT_ON_PRODUCT,
    generation_source: entitlement.generationSource,
    entitled_entity_quantity_distribution: entitlement.entitledCatalogue.map((group, index) => ({
      quantity: group.quantity,
      entities: getEntitiesForPayload(group.entities, entitlement.metaData.entitledCatalogue[index])
    })),
    excluded_entity_ids: getEntitlementExcludedForPayload(state),
    prerequisite_excluded_entity_ids: getPrerequisiteExcludedForPayload(state),
    entitled_product_count: entitlement.entitledProductCount,
    maximum_concession_amount: entitlement.maximumConcessionAmount || (isEditMode ? -1 : null),
    application_target: ApplicationTarget.ENTITLED,
    prerequisite_subtotal_range: prerequisiteSubtotalRange ?? (isEditMode ? -1 : null),
    prerequisite_max_order_value: prerequisiteMaxOrderValue ?? (isEditMode ? -1 : null),
    prerequisite_count_to_entitlement:
      prerequisite.prerequisiteType === PrerequisiteType.QUANTITY
        ? prerequisite.prerequisiteCountToEntitlement ?? (isEditMode ? -1 : null)
        : isEditMode
        ? -1
        : null,
    prerequisite_max_count_for_entitlement: prerequisiteMaxCountForEntitlement ?? (isEditMode ? -1 : null),
    prerequisite_entity_quantity_distribution: prerequisiteDistribution,
    multiple_application_options: {
      enable: Boolean(entitlement.maxUsesPerOrder),
      number_of_times_to_apply: entitlement.maxUsesPerOrder ?? null
    },
    metadata: {
      entitledCatalogue: entitlement.metaData.entitledCatalogue,
      excludedCatalogue: entitlement.metaData.excludedCatalogue,
      prerequisiteCatalogue: prerequisiteCatalboueMetaData,
      prerequisiteExcludedCatalogue: prerequisite.metaData?.excludedCatalogue || []
    }
  }
}

const getAmountOffDiscountPayload = (state: DiscountFormV2StateType, isEditMode?: boolean) => {
  const { uiConfig, entitlement, prerequisite, configuration } = state
  if (
    uiConfig.type !== UiConfigDiscountType.AMOUNT_OFF ||
    entitlement.type !== CouponType.AMOUNT ||
    prerequisite.type === CouponType.BUNDLE
  )
    return

  let prerequisiteDistribution: PrerequisiteDistributionType = []
  let prerequisiteSubtotalRange = null
  let prerequisiteMaxOrderValue = null
  let prerequisiteCountToEntitlement = null
  let prerequisiteMaxCountForEntitlement = null
  let prerequisiteCatalboueMetaData: CatalogueDataType[][] = []

  prerequisiteDistribution = prerequisite.prerequisiteCatalogue.map((item, index) => ({
    ...(item.type === PrerequisiteType.PRICE
      ? { price: item.price, max_price: item.maxPrice }
      : { quantity: item.quantity, max_quantity: item.maxQuantity }),
    entities: getEntitiesForPayload(item.entities, prerequisite.metaData.prerequisiteCatalogue[index])
  }))
  prerequisiteCatalboueMetaData = prerequisite.metaData.prerequisiteCatalogue

  if (prerequisite.prerequisiteType === PrerequisiteType.PRICE) {
    if (prerequisite.purchaseConditionAmountType === PurchaseConditionAmountType.ENTIRE_CART) {
      prerequisiteSubtotalRange = prerequisite.prerequisiteSubtotalRange
      prerequisiteMaxOrderValue = prerequisite.prerequisiteMaxOrderValue
      prerequisiteDistribution = []
      prerequisiteCatalboueMetaData = []
    }
  } else if (prerequisite.prerequisiteType === PrerequisiteType.QUANTITY) {
    if (prerequisite.purchaseConditionAmountType === PurchaseConditionAmountType.ENTIRE_CART) {
      prerequisiteCountToEntitlement = prerequisite.prerequisiteCountToEntitlement
      prerequisiteMaxCountForEntitlement = prerequisite.prerequisiteMaxCountForEntitlement
      prerequisiteDistribution = []
      prerequisiteCatalboueMetaData = []
    }
  }

  return {
    coupon_type: configuration.couponType,
    amount_type: entitlement.amountType,
    concession_amount: entitlement.concessionAmount,
    bxgy_product_strategy: bxgyProductStrategy.DISCOUNT_ON_PRODUCT,
    generation_source: entitlement.generationSource,
    entitled_entity_quantity_distribution: entitlement.entitledCatalogue.map((group, index) => ({
      quantity: group.quantity,
      entities: getEntitiesForPayload(group.entities, entitlement.metaData.entitledCatalogue[index])
    })),
    excluded_entity_ids: getEntitlementExcludedForPayload(state),
    prerequisite_excluded_entity_ids: getPrerequisiteExcludedForPayload(state),
    entitled_product_count: entitlement.entitledProductCount || null,
    maximum_concession_amount: entitlement.maximumConcessionAmount || (isEditMode ? -1 : null),
    application_target: entitlement.applicationTarget,
    prerequisite_subtotal_range: prerequisiteSubtotalRange ?? (isEditMode ? -1 : null),
    prerequisite_max_order_value: prerequisiteMaxOrderValue ?? (isEditMode ? -1 : null),
    prerequisite_count_to_entitlement:
      prerequisite.prerequisiteType === PrerequisiteType.QUANTITY
        ? prerequisite.prerequisiteCountToEntitlement ?? (isEditMode ? -1 : null)
        : isEditMode
        ? -1
        : null,
    prerequisite_max_count_for_entitlement: prerequisiteMaxCountForEntitlement ?? (isEditMode ? -1 : null),
    prerequisite_entity_quantity_distribution: prerequisiteDistribution,
    multiple_application_options: {
      enable: Boolean(entitlement.maxUsesPerOrder),
      number_of_times_to_apply: entitlement.maxUsesPerOrder
    },
    metadata: {
      entitledCatalogue: entitlement.metaData.entitledCatalogue,
      excludedCatalogue: entitlement.metaData.excludedCatalogue,
      prerequisiteCatalogue: prerequisiteCatalboueMetaData,
      prerequisiteExcludedCatalogue: prerequisite.metaData?.excludedCatalogue || []
    }
  }
}

const getAmountOffUptoDiscountPayload = (state: DiscountFormV2StateType, isEditMode?: boolean) => {
  const { uiConfig, entitlement, prerequisite, configuration } = state
  if (
    uiConfig.type !== UiConfigDiscountType.AMOUNT_OFF_UPTO ||
    entitlement.type !== CouponType.AMOUNT ||
    prerequisite.type === CouponType.BUNDLE
  )
    return

  let prerequisiteDistribution: PrerequisiteDistributionType = []
  let prerequisiteSubtotalRange = null
  let prerequisiteMaxOrderValue = null
  let prerequisiteCountToEntitlement = null
  let prerequisiteMaxCountForEntitlement = null
  let prerequisiteCatalboueMetaData: CatalogueDataType[][] = []

  prerequisiteDistribution = prerequisite.prerequisiteCatalogue.map((item, index) => ({
    ...(item.type === PrerequisiteType.PRICE
      ? { price: item.price, max_price: item.maxPrice }
      : { quantity: item.quantity, max_quantity: item.maxQuantity }),
    entities: getEntitiesForPayload(item.entities, prerequisite.metaData.prerequisiteCatalogue[index])
  }))
  prerequisiteCatalboueMetaData = prerequisite.metaData.prerequisiteCatalogue

  if (prerequisite.prerequisiteType === PrerequisiteType.PRICE) {
    if (prerequisite.purchaseConditionAmountType === PurchaseConditionAmountType.ENTIRE_CART) {
      prerequisiteSubtotalRange = prerequisite.prerequisiteSubtotalRange
      prerequisiteMaxOrderValue = prerequisite.prerequisiteMaxOrderValue
      prerequisiteDistribution = []
      prerequisiteCatalboueMetaData = []
    }
  } else if (prerequisite.prerequisiteType === PrerequisiteType.QUANTITY) {
    if (prerequisite.purchaseConditionAmountType === PurchaseConditionAmountType.ENTIRE_CART) {
      prerequisiteCountToEntitlement = prerequisite.prerequisiteCountToEntitlement
      prerequisiteMaxCountForEntitlement = prerequisite.prerequisiteMaxCountForEntitlement
      prerequisiteDistribution = []
      prerequisiteCatalboueMetaData = []
    }
  }

  return {
    coupon_type: configuration.couponType,
    amount_type: entitlement.amountType,
    concession_amount: entitlement.concessionAmount,
    bxgy_product_strategy: bxgyProductStrategy.DISCOUNT_ON_PRODUCT,
    generation_source: entitlement.generationSource,
    entitled_entity_quantity_distribution: entitlement.entitledCatalogue.map((group, index) => ({
      quantity: group.quantity,
      entities: getEntitiesForPayload(group.entities, entitlement.metaData.entitledCatalogue[index])
    })),
    excluded_entity_ids: getEntitlementExcludedForPayload(state),
    prerequisite_excluded_entity_ids: getPrerequisiteExcludedForPayload(state),
    entitled_product_count: entitlement.entitledProductCount,
    maximum_concession_amount: entitlement.maximumConcessionAmount || (isEditMode ? -1 : null),
    application_target: entitlement.applicationTarget,
    prerequisite_subtotal_range: prerequisiteSubtotalRange ?? (isEditMode ? -1 : null),
    prerequisite_max_order_value: prerequisiteMaxOrderValue ?? (isEditMode ? -1 : null),
    prerequisite_count_to_entitlement:
      prerequisite.prerequisiteType === PrerequisiteType.QUANTITY
        ? prerequisite.prerequisiteCountToEntitlement ?? (isEditMode ? -1 : null)
        : isEditMode
        ? -1
        : null,
    prerequisite_max_count_for_entitlement: prerequisiteMaxCountForEntitlement ?? (isEditMode ? -1 : null),
    prerequisite_entity_quantity_distribution: prerequisiteDistribution,
    multiple_application_options: {
      enable: Boolean(entitlement.maxUsesPerOrder),
      number_of_times_to_apply: entitlement.maxUsesPerOrder
    },
    metadata: {
      entitledCatalogue: entitlement.metaData.entitledCatalogue,
      excludedCatalogue: entitlement.metaData.excludedCatalogue,
      prerequisiteCatalogue: prerequisiteCatalboueMetaData,
      prerequisiteExcludedCatalogue: prerequisite.metaData?.excludedCatalogue || []
    }
  }
}

const getShippingDiscountPayload = (state: DiscountFormV2StateType, isEditMode?: boolean) => {
  const { uiConfig, entitlement, prerequisite } = state
  if (
    uiConfig.type !== UiConfigDiscountType.SHIPPING ||
    entitlement.type !== CouponType.SHIPPING ||
    prerequisite.type === CouponType.BUNDLE
  )
    return

  let prerequisiteDistribution: PrerequisiteDistributionType = []
  let prerequisiteSubtotalRange = null
  let prerequisiteMaxOrderValue = null
  let prerequisiteCountToEntitlement = null
  let prerequisiteMaxCountForEntitlement = null
  let prerequisiteCatalboueMetaData: CatalogueDataType[][] = []

  prerequisiteDistribution = prerequisite.prerequisiteCatalogue.map((item, index) => ({
    ...(item.type === PrerequisiteType.PRICE
      ? { price: item.price, max_price: item.maxPrice }
      : { quantity: item.quantity, max_quantity: item.maxQuantity }),
    entities: getEntitiesForPayload(item.entities, prerequisite.metaData.prerequisiteCatalogue[index])
  }))
  prerequisiteCatalboueMetaData = prerequisite.metaData.prerequisiteCatalogue

  if (prerequisite.prerequisiteType === PrerequisiteType.PRICE) {
    if (prerequisite.purchaseConditionAmountType === PurchaseConditionAmountType.ENTIRE_CART) {
      prerequisiteSubtotalRange = prerequisite.prerequisiteSubtotalRange
      prerequisiteMaxOrderValue = prerequisite.prerequisiteMaxOrderValue
      prerequisiteDistribution = []
      prerequisiteCatalboueMetaData = []
    }
  } else if (prerequisite.prerequisiteType === PrerequisiteType.QUANTITY) {
    if (prerequisite.purchaseConditionAmountType === PurchaseConditionAmountType.ENTIRE_CART) {
      prerequisiteCountToEntitlement = prerequisite.prerequisiteCountToEntitlement
      prerequisiteMaxCountForEntitlement = prerequisite.prerequisiteMaxCountForEntitlement
      prerequisiteDistribution = []
      prerequisiteCatalboueMetaData = []
    }
  }

  return {
    coupon_type: CouponType.SHIPPING,
    amount_type: entitlement.amountType,
    concession_amount: entitlement.concessionAmount,
    bxgy_product_strategy: bxgyProductStrategy.DISCOUNT_ON_PRODUCT,
    generation_source: generationSourceType.SINGLE,
    entitled_entity_quantity_distribution: null,
    excluded_entity_ids: [],
    prerequisite_excluded_entity_ids: getPrerequisiteExcludedForPayload(state),
    maximum_concession_amount: entitlement.maximumConcessionAmount || (isEditMode ? -1 : null),
    entitled_product_count: null,
    application_target: ApplicationTarget.ALL,
    prerequisite_subtotal_range: prerequisiteSubtotalRange ?? (isEditMode ? -1 : null),
    prerequisite_max_order_value: prerequisiteMaxOrderValue ?? (isEditMode ? -1 : null),
    prerequisite_count_to_entitlement:
      prerequisite.prerequisiteType === PrerequisiteType.QUANTITY
        ? prerequisite.prerequisiteCountToEntitlement ?? (isEditMode ? -1 : null)
        : isEditMode
        ? -1
        : null,
    prerequisite_max_count_for_entitlement: prerequisiteMaxCountForEntitlement ?? (isEditMode ? -1 : null),
    prerequisite_entity_quantity_distribution: prerequisiteDistribution,
    metadata: {
      entitledCatalogue: [],
      excludedCatalogue: [],
      prerequisiteCatalogue: prerequisiteCatalboueMetaData,
      prerequisiteExcludedCatalogue: prerequisite.metaData?.excludedCatalogue || []
    },
    multiple_application_options: {
      enable: false,
      number_of_times_to_apply: null
    }
  }
}

const getExitCouponDiscountPayload = (state: DiscountFormV2StateType, isEditMode?: boolean) => {
  const { uiConfig, entitlement, prerequisite, configuration } = state
  if (
    uiConfig.type !== UiConfigDiscountType.EXIT_COUPON ||
    entitlement.type !== CouponType.AMOUNT ||
    prerequisite.type === CouponType.BUNDLE
  )
    return

  let prerequisiteDistribution: PrerequisiteDistributionType = []
  let prerequisiteSubtotalRange = null
  let prerequisiteMaxOrderValue = null
  let prerequisiteCountToEntitlement = null
  let prerequisiteMaxCountForEntitlement = null
  let prerequisiteCatalboueMetaData: CatalogueDataType[][] = []

  prerequisiteDistribution = prerequisite.prerequisiteCatalogue.map((item, index) => ({
    ...(item.type === PrerequisiteType.PRICE
      ? { price: item.price, max_price: item.maxPrice }
      : { quantity: item.quantity, max_quantity: item.maxQuantity }),
    entities: getEntitiesForPayload(item.entities, prerequisite.metaData.prerequisiteCatalogue[index])
  }))
  prerequisiteCatalboueMetaData = prerequisite.metaData.prerequisiteCatalogue

  if (prerequisite.prerequisiteType === PrerequisiteType.PRICE) {
    if (prerequisite.purchaseConditionAmountType === PurchaseConditionAmountType.ENTIRE_CART) {
      prerequisiteSubtotalRange = prerequisite.prerequisiteSubtotalRange
      prerequisiteMaxOrderValue = prerequisite.prerequisiteMaxOrderValue
      prerequisiteDistribution = []
      prerequisiteCatalboueMetaData = []
    }
  } else if (prerequisite.prerequisiteType === PrerequisiteType.QUANTITY) {
    if (prerequisite.purchaseConditionAmountType === PurchaseConditionAmountType.ENTIRE_CART) {
      prerequisiteCountToEntitlement = prerequisite.prerequisiteCountToEntitlement
      prerequisiteMaxCountForEntitlement = prerequisite.prerequisiteMaxCountForEntitlement
      prerequisiteDistribution = []
      prerequisiteCatalboueMetaData = []
    }
  }

  return {
    coupon_type: configuration.couponType,
    amount_type: entitlement.amountType,
    concession_amount: entitlement.concessionAmount,
    bxgy_product_strategy: bxgyProductStrategy.DISCOUNT_ON_PRODUCT,
    generation_source: entitlement.generationSource,
    entitled_entity_quantity_distribution: entitlement.entitledCatalogue.map((group, index) => ({
      quantity: group.quantity,
      entities: getEntitiesForPayload(group.entities, entitlement.metaData.entitledCatalogue[index])
    })),
    excluded_entity_ids: getEntitlementExcludedForPayload(state),
    prerequisite_excluded_entity_ids: getPrerequisiteExcludedForPayload(state),
    entitled_product_count: entitlement.entitledProductCount || null,
    maximum_concession_amount: entitlement.maximumConcessionAmount || null,
    application_target: entitlement.applicationTarget,
    prerequisite_subtotal_range: prerequisiteSubtotalRange ?? (isEditMode ? -1 : null),
    prerequisite_max_order_value: prerequisiteMaxOrderValue ?? (isEditMode ? -1 : null),
    prerequisite_count_to_entitlement:
      prerequisite.prerequisiteType === PrerequisiteType.QUANTITY
        ? prerequisite.prerequisiteCountToEntitlement ?? (isEditMode ? -1 : null)
        : isEditMode
        ? -1
        : null,
    prerequisite_max_count_for_entitlement: prerequisiteMaxCountForEntitlement ?? (isEditMode ? -1 : null),
    prerequisite_entity_quantity_distribution: prerequisiteDistribution,
    multiple_application_options: {
      enable: Boolean(entitlement.maxUsesPerOrder),
      number_of_times_to_apply: entitlement.maxUsesPerOrder
    },
    metadata: {
      entitledCatalogue: entitlement.metaData.entitledCatalogue,
      excludedCatalogue: entitlement.metaData.excludedCatalogue,
      prerequisiteCatalogue: prerequisiteCatalboueMetaData,
      prerequisiteExcludedCatalogue: prerequisite.metaData?.excludedCatalogue || []
    }
  }
}

const createPreviewPayload = (preview: DiscountPreviewV2Type) => {
  return {
    header: preview.header || '',
    description: preview.description || '',
    long_description: preview.longDescription || []
  }
}

const createApplicationMethodPayload = (applicationMethod: DiscountApplicationMethodFormV2Type) => {
  if (applicationMethod.method === DiscountMethod.AUTOMATIC) {
    return {
      is_auto_applicable: true,
      title: applicationMethod.title
    }
  }

  return {
    is_auto_applicable: false,
    code: applicationMethod.discountCode,
    title: applicationMethod.title || applicationMethod.discountCode
  }
}

const createAvailabilityPayload = (availability: AvailabilityFormV2Type, isEditMode?: boolean) => {
  return {
    enabled_channels: availability.enabledChannelIds || ['web'],
    start_date: availability.startDate ?? Date.now(),
    end_date: availability.endDate ?? (isEditMode ? -1 : null),
    is_discoverable: availability.discoverable,
    is_active: availability.active
  }
}

const createUsageLimitPayload = (usageLimit: UsageLimitFormV2Type, isEditMode?: boolean) => {
  return {
    max_applicable_count: usageLimit.maxApplicableCount ?? (isEditMode ? -1 : null),
    max_applicable_per_user_count: usageLimit.maxApplicablePerUserCount ?? (isEditMode ? -1 : null)
  }
}

const createDiscountStackabilityPayload = (stackability: DiscountStackabilityFormV2Type) => {
  switch (stackability.stackMode) {
    case DiscountStackMode.ALL:
      return {
        stack_config: {
          stack_type: DiscountStackMode.ALL,
          includes: ['*']
        }
      }
    case DiscountStackMode.NONE:
      return {
        stack_config: {
          stack_type: DiscountStackMode.NONE,
          includes: []
        }
      }
    case DiscountStackMode.CUSTOM:
      return {
        stack_config: {
          stack_type: DiscountStackMode.CUSTOM,
          includes: stackability.customStackableDiscountIds
        }
      }
    default:
      return {
        stack_type: DiscountStackMode.ALL,
        includes: ['*']
      }
  }
}

const createCustomerEligibilityPayload = (eligibility: CustomerEligibilityFormV2Type) => {
  const basePayload = {
    prerequisite_user_order_cohort: {
      lifetime_min_order_count: null,
      lifetime_max_order_count: null
    },
    segment_ids: [],
    prerequisite_customer_ids: [],
    customer_specific: false,
    utm_params: {},
    override_pre_evaluated_segment: true,
    file_meta: {
      file_url: null,
      file_key: null,
      download_url: null,
      file_name: null,
      size: null
    }
  }

  switch (eligibility.type) {
    case CustomerEligibilityV2Type.ALL_CUSTOMERS:
      return basePayload

    case CustomerEligibilityV2Type.FIRST_TIME_CUSTOMERS:
      return {
        ...basePayload,
        prerequisite_user_order_cohort: {
          lifetime_min_order_count: 0,
          lifetime_max_order_count: 0
        }
      }

    case CustomerEligibilityV2Type.LIFETIME_ORDER_COUNT:
      return {
        ...basePayload,
        prerequisite_user_order_cohort: {
          lifetime_min_order_count: eligibility.prerequisiteUserOrderCohort.lifetimeMinOrderCount,
          lifetime_max_order_count: eligibility.prerequisiteUserOrderCohort.lifetimeMaxOrderCount
        }
      }

    case CustomerEligibilityV2Type.SPECIFIC_CUSTOMERS:
      return {
        ...basePayload,
        customer_specific: true,
        ...(eligibility.fileMeta && {
          file_meta: {
            file_url: eligibility.fileMeta.fileUrl,
            file_name: eligibility.fileMeta.fileName,
            size: eligibility.fileMeta.size,
            download_url: eligibility.fileMeta.downloadUrl
          }
        })
      }

    case CustomerEligibilityV2Type.CUSTOMER_SPECIFIC:
      return {
        ...basePayload,
        customer_specific: false,
        segment_ids: eligibility.segmentIds,
        override_pre_evaluated_segment: eligibility.overridePreEvaluatedSegment
      }

    case CustomerEligibilityV2Type.UTM_PARAMS:
      return {
        ...basePayload,
        utm_params: {
          source: eligibility.utmParams.source,
          medium: eligibility.utmParams.medium,
          campaign: eligibility.utmParams.campaign,
          content: eligibility.utmParams.content
        }
      }

    default:
      return basePayload
  }
}
